import React, { useState, useEffect } from 'react';
import { Save, Shield, Bell, Globe, Lock, Trash2, Eye, Cpu, Zap, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';
import { API_URL } from '../api';

const AdminSettingItem = ({ icon: Icon, title, description, children, danger }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between p-8 gap-6 group hover:bg-gray-50/50 transition-all border-b border-gray-50 last:border-0">
    <div className="flex items-start gap-5">
      <div className={cn(
        "w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm shrink-0 mt-1",
        danger ? "bg-red-50 text-red-600" : "bg-white text-gray-600 border border-gray-100 group-hover:border-brand-indigo/20 group-hover:text-brand-indigo"
      )}>
        <Icon size={24} />
      </div>
      <div className="max-w-md">
        <h3 className={cn("text-lg font-bold tracking-tight", danger ? "text-red-600" : "text-gray-900")}>{title}</h3>
        <p className="text-sm text-gray-500 font-medium leading-relaxed mt-1">{description}</p>
      </div>
    </div>
    <div className="shrink-0 flex items-center">
      {children}
    </div>
  </div>
);

const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={() => onChange(!enabled)}
    className={cn(
      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-indigo/20 focus:ring-offset-2",
      enabled ? "bg-brand-indigo" : "bg-gray-200"
    )}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
        enabled ? "translate-x-6" : "translate-x-1"
      )}
    />
  </button>
);

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    newRegistrations: true,
    emailVerification: true,
    publicDiscovery: true,
    autoModeration: true,
    liveAnalytics: true,
    platformTheme: 'Dynamic',
    retentionDays: 90
  });

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8 text-center md:text-left">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Platform Settings</h1>
          <p className="text-gray-500 font-medium mt-1 text-sm">Global configuration for Chooziq's infrastructure and experience.</p>
        </div>
        <button className="hidden md:flex items-center gap-2 bg-brand-indigo text-white px-5 py-2.5 rounded-2xl font-bold text-sm shadow-xl shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all active:scale-95">
          <Save size={18} />
          Save Changes
        </button>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.3em] mb-3 px-4">System Status</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <AdminSettingItem 
              icon={Shield} 
              title="Maintenance Mode" 
              description="Temporarily disable platform access for all non-admin users. Knowledge remains preserved."
            >
              <Toggle enabled={settings.maintenanceMode} onChange={() => handleToggle('maintenanceMode')} />
            </AdminSettingItem>
            <AdminSettingItem 
              icon={Lock} 
              title="New User Registration" 
              description="Control whether new users can create accounts on the platform."
            >
              <Toggle enabled={settings.newRegistrations} onChange={() => handleToggle('newRegistrations')} />
            </AdminSettingItem>
            <AdminSettingItem 
              icon={Bell} 
              title="Email Verification" 
              description="Require users to verify their email address before participating in rooms."
            >
              <Toggle enabled={settings.emailVerification} onChange={() => handleToggle('emailVerification')} />
            </AdminSettingItem>
          </div>
        </section>

        <section>
          <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.3em] mb-3 px-4">Participation & Content</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <AdminSettingItem 
              icon={Eye} 
              title="Public Discovery" 
              description="Allow public communities and rooms to be featured on the main homepage feed."
            >
              <Toggle enabled={settings.publicDiscovery} onChange={() => handleToggle('publicDiscovery')} />
            </AdminSettingItem>
            <AdminSettingItem 
              icon={Cpu} 
              title="AI Auto-Moderation" 
              description="Enable automated spam detection and content filtering for live interactions."
            >
              <Toggle enabled={settings.autoModeration} onChange={() => handleToggle('autoModeration')} />
            </AdminSettingItem>
            <AdminSettingItem 
              icon={Radio} 
              title="Real-time Analytics" 
              description="Enable high-frequency interaction tracking and engagement monitoring."
            >
              <Toggle enabled={settings.liveAnalytics} onChange={() => handleToggle('liveAnalytics')} />
            </AdminSettingItem>
          </div>
        </section>

        <section>
          <h2 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.3em] mb-3 px-4">Infrastructure</h2>
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Platform Data Retention</label>
                <select className="w-full bg-gray-50 border-none rounded-2xl py-3.5 px-4 font-bold text-sm focus:ring-2 focus:ring-brand-indigo/10 transition-all outline-none">
                  <option>30 Days</option>
                  <option>90 Days</option>
                  <option>1 Year</option>
                  <option>Forever</option>
                </select>
                <p className="mt-2 text-xxs text-gray-400 font-bold uppercase tracking-widest">Controls when archived data is purged</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Primary API Gateway</label>
                <input 
                  type="text" 
                  value={API_URL} 
                  readOnly
                  className="w-full bg-gray-50 border-none rounded-2xl py-3.5 px-4 font-bold text-sm text-gray-400 cursor-not-allowed"
                />
                <p className="mt-2 text-xxs text-gray-400 font-bold uppercase tracking-widest">Active Infrastructure Endpoint</p>
              </div>
            </div>
          </div>
        </section>

        <button className="w-full md:hidden bg-brand-indigo text-white py-4 rounded-2xl font-bold shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 mb-8">
          <Save size={20} />
          Save All Changes
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
